'use client'

import { useTranslations } from 'next-intl'
import { startTransition, useMemo, useOptimistic } from 'react'
import styled from 'styled-components'

import { Box, Chips, Menu, spacing, styles } from '@fortum/elemental-ui'

import { useRouter } from '@/i18n/navigation'
import type { ArticleCategoryEntry } from '@/shared/contentful/types'
import { useTheme } from '@/shared/hooks/useTheme'
import { routes } from '@/shared/routes'
import type { PageCustomerType } from '@/shared/types/Common'

import { slugifyTitle } from '../utils'

const ALL_ID = 'all'

const CHIP_COUNT = 5

export type ArticleFiltersProps = {
  categories: ArticleCategoryEntry[]
  selectedCategoryId: string | undefined
  scope: PageCustomerType
}

export const ArticleFilters = ({ categories, selectedCategoryId, scope }: ArticleFiltersProps) => {
  const { colors } = useTheme()
  const t = useTranslations('articleFrontPage')
  const router = useRouter()

  // Top five categories sorted by usage
  const chipItems = useMemo(() => {
    const allItem = { label: t('allCategories'), value: ALL_ID }
    const categoryItems = categories.slice(0, CHIP_COUNT).map((category) => ({
      label: category.title ?? '',
      value: category.sys.id,
    }))
    return [allItem].concat(categoryItems)
  }, [t, categories])

  // All categories sorted alphabetically
  const menuItems = useMemo(() => {
    const allItem = { name: t('allCategories'), value: ALL_ID }
    const categoryItems = categories
      .map((category) => ({
        name: category.title ?? '',
        value: category.sys.id,
      }))
      .toSorted((a, b) => a.name.localeCompare(b.name))
    return [allItem].concat(categoryItems)
  }, [t, categories])

  const selectCategory = (id: string | undefined) => {
    const isSelected = id === selectedCategoryId
    const category = categories.find((category) => category.sys.id === id)
    startTransition(() => {
      setOptimisticId(!isSelected ? id : undefined)
      router.push({
        pathname: scope === 'private' ? routes.BLOG_CATEGORY : routes.ENTERPRISE_BLOG_CATEGORY,
        params: { slug: slugifyTitle(category?.title) || 'all', page: '1' },
      })
    })
  }

  const [optimisticSelectedId, setOptimisticId] = useOptimistic(
    selectedCategoryId,
    (_state, newValue: string | undefined) => newValue,
  )

  const selectedCategory = useMemo(() => {
    return categories.find((category) => category.sys.id === optimisticSelectedId)
  }, [categories, optimisticSelectedId])

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" gap={spacing.xxs}>
      <StyledChips
        items={chipItems}
        active={optimisticSelectedId ? [optimisticSelectedId] : [ALL_ID]}
        onChange={(ids) => selectCategory(ids.at(0))}
        aria-label={t('chipsAriaLabel')}
        data-testid="article-category-filters"
        display={{ default: 'none', m: 'flex' }}
      />
      <Menu<string>
        items={menuItems}
        selected={optimisticSelectedId}
        onSelect={(id) => selectCategory(id)}
        aria-label={t('menuAriaLabel')}
        label={selectedCategory?.title ?? t('allCategories')}
        variant="condensed"
        flexShrink={0}
        buttonProps={{
          backgroundColor: colors.backgroundPrimary,
          border: `2px solid ${colors.borderPrimary}`,
          width: '100%',
        }}
        width={{ default: '100%', m: 'auto' }}
        id="article-category-menu"
        data-testid="article-category-menu"
      />
    </Box>
  )
}

const StyledChips = styled(Chips<string>)`
  ${styles.breakpointMax.xl} {
    /* Hide chips after the third on smaller screens */
    button:nth-child(n + 4) {
      display: none;
    }
  }
`
